/**
 * logger.ts
 * 前端统一日志：内存缓冲 + 批量上报到后端 /api/client-logs（上云、不做本地持久化）。
 * 错误/警告级别默认转发 Sentry（Sentry 已自动捕获的来源可通过 opts.sentry=false 跳过）。
 * 绝不写入 localStorage / IndexedDB。
 */
import { captureError, captureMessage } from './sentry';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface ClientLogEntry {
  level: LogLevel;
  message: string;
  event?: string;
  timestamp: string;
  url: string;
  requestId?: string;
  data?: unknown;
}

const ENDPOINT = '/api/client-logs';
const MAX_QUEUE = 50; // 达到即触发上报
const FLUSH_INTERVAL_MS = 15_000;
const INFO_SAMPLE_RATE = 0.5; // info 级别按比例上报，降低噪声与流量
const MAX_DATA_CHARS = 4000;

const SENSITIVE_KEYS = ['password', 'apikey', 'api_key', 'token', 'authorization', 'secret', 'cookie'];

const queue: ClientLogEntry[] = [];
let lastRequestId: string | undefined;
let started = false;

/** 浅层脱敏：剔除敏感字段并截断超长数据。 */
function scrub(data: unknown): unknown {
  if (data == null) return data;
  if (data instanceof Error) {
    return { name: data.name, message: data.message, stack: data.stack };
  }
  if (typeof data !== 'object') return data;
  try {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(data as Record<string, unknown>)) {
      if (SENSITIVE_KEYS.includes(k.toLowerCase())) {
        out[k] = '[REDACTED]';
      } else if (v && typeof v === 'object') {
        out[k] = '[object]';
      } else {
        out[k] = v;
      }
    }
    const str = JSON.stringify(out);
    if (str.length > MAX_DATA_CHARS) return { _truncated: true, preview: str.slice(0, MAX_DATA_CHARS) };
    return out;
  } catch {
    return '[unserializable]';
  }
}

function send(entries: ClientLogEntry[]): void {
  if (entries.length === 0) return;
  const payload = JSON.stringify({ logs: entries });
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([payload], { type: 'application/json' });
      if (navigator.sendBeacon(ENDPOINT, blob)) return;
    }
  } catch {
    /* fall through to fetch */
  }
  // 回退：keepalive fetch（页面卸载也尽量送达）。
  try {
    void fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
      credentials: 'include',
    }).catch(() => { /* logging must never break the app */ });
  } catch {
    /* give up silently: logging must never break the app */
  }
}

export function flush(): void {
  if (queue.length === 0) return;
  const batch = queue.splice(0, queue.length);
  send(batch);
}

function enqueue(entry: ClientLogEntry): void {
  queue.push(entry);
  if (queue.length >= MAX_QUEUE) flush();
}

interface RecordOpts {
  /** 是否转发 Sentry。默认 true；对 Sentry 已自动捕获的来源（全局异常、错误边界）设为 false 以避免重复上报。 */
  sentry?: boolean;
}

function record(level: LogLevel, message: string, data?: unknown, event?: string, opts: RecordOpts = {}): void {
  if (level === 'info' && Math.random() > INFO_SAMPLE_RATE) return;

  const entry: ClientLogEntry = {
    level,
    message,
    event,
    timestamp: new Date().toISOString(),
    url: typeof location !== 'undefined' ? location.hash || location.pathname : '',
    requestId: lastRequestId,
    data: data === undefined ? undefined : scrub(data),
  };
  enqueue(entry);

  // 错误 / 警告转发 Sentry（除非调用方声明已由 Sentry 自动捕获）。
  if (opts.sentry === false) return;
  if (level === 'error') {
    if (data instanceof Error) captureError(data, { message, ...(event ? { event } : {}) });
    else captureMessage(message, 'error', { data: entry.data, event });
  } else if (level === 'warn') {
    captureMessage(message, 'warning', { data: entry.data, event });
  }
}

export const logger = {
  debug: (message: string, data?: unknown) => record('debug', message, data),
  info: (message: string, data?: unknown) => record('info', message, data),
  warn: (message: string, data?: unknown) => record('warn', message, data),
  error: (message: string, data?: unknown, opts?: RecordOpts) => record('error', message, data, undefined, opts),
  event: (event: string, level: LogLevel, data?: unknown) => record(level, event, data, event),
  /** 记录最近一次请求的 requestId（由 API 层从响应头 X-Request-Id 写入），用于前后端关联。 */
  setRequestId: (id: string | undefined) => { if (id) lastRequestId = id; },
};

/** 挂载全局异常捕获、定时/卸载时批量上报。幂等。 */
export function initClientLogging(): void {
  if (started || typeof window === 'undefined') return;
  started = true;

  // Sentry 默认集成已自动捕获全局异常/未处理拒绝，这里仅转发到后端，避免重复上报。
  window.addEventListener('error', (e: ErrorEvent) => {
    record('error', e.message || 'uncaught error', e.error ?? { filename: e.filename, lineno: e.lineno, colno: e.colno }, undefined, { sentry: false });
  });

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const reason = e.reason;
    record(
      'error',
      reason instanceof Error ? reason.message : 'unhandled promise rejection',
      reason instanceof Error ? reason : { reason },
      undefined,
      { sentry: false },
    );
  });

  const flushOnHide = () => { if (document.visibilityState === 'hidden') flush(); };
  document.addEventListener('visibilitychange', flushOnHide);
  window.addEventListener('pagehide', flush);

  setInterval(flush, FLUSH_INTERVAL_MS);
}
